/**
 * AdsManager — secret-admin section that CRUDs the multi-ad rotation pool.
 *  - List of all campaigns with stats (impressions, unique viewers, clicks)
 *  - Add / edit / disable / delete
 *  - Per-ad caps (max_clicks, max_unique_impressions). When a cap is hit
 *    the ad auto-falls-out of rotation; LoveAll + Advertise-Here house ads
 *    always remain.
 *  - URL-based image only (no file upload) — admin pastes a public URL.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Plus, Trash2, Pencil, ImageIcon, MousePointerClick, Eye } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  ad_id: null,
  name: '',
  image_url: '',
  click_url: '',
  max_clicks: 0,
  max_unique_impressions: 0,
  enabled: true,
};

export default function AdsManager({ apiCall }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiCall('/api/admin-secret/ads');
      setAds(d.ads || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (ad) => { setForm({ ...EMPTY, ...ad }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.image_url.trim()) {
      toast.error('Name and Image URL are required'); return;
    }
    setSaving(true);
    try {
      if (form.ad_id) {
        await apiCall(`/api/admin-secret/ads/${form.ad_id}`, {
          method: 'PUT', body: JSON.stringify(form),
        });
        toast.success('Ad updated');
      } else {
        await apiCall('/api/admin-secret/ads', {
          method: 'POST', body: JSON.stringify(form),
        });
        toast.success('Ad created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete "${ad.name}"? Historical stats are retained.`)) return;
    try {
      await apiCall(`/api/admin-secret/ads/${ad.ad_id}`, { method: 'DELETE' });
      toast.success('Ad deleted');
      load();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const toggleEnabled = async (ad) => {
    try {
      await apiCall(`/api/admin-secret/ads/${ad.ad_id}`, {
        method: 'PUT', body: JSON.stringify({ enabled: !ad.enabled }),
      });
      load();
    } catch (e) { toast.error(e.message || 'Toggle failed'); }
  };

  return (
    <section data-testid="ads-manager-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Ad Campaigns</h2>
        <Button size="sm" onClick={openCreate} data-testid="ad-create-btn">
          <Plus className="h-4 w-4 mr-1" /> New Campaign
        </Button>
      </div>

      <div className="space-y-2">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && ads.length === 0 && (
          <Card><CardContent className="p-4 text-sm text-muted-foreground">
            No campaigns yet. The slot will rotate the LoveAll + "Advertise Here" defaults until you add one.
          </CardContent></Card>
        )}
        {ads.map((ad) => (
          <Card key={ad.ad_id} className={!ad.in_rotation ? 'opacity-60' : ''} data-testid={`ad-row-${ad.ad_id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              {ad.image_url ? (
                <img src={ad.image_url} alt={ad.name} className="h-14 w-20 object-cover rounded shrink-0" />
              ) : (
                <div className="h-14 w-20 rounded bg-muted flex items-center justify-center shrink-0">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm truncate">{ad.name}</span>
                  {ad.in_rotation
                    ? <Badge variant="default" className="text-[9px]">LIVE</Badge>
                    : <Badge variant="outline" className="text-[9px]">{ad.enabled ? 'CAPPED' : 'PAUSED'}</Badge>
                  }
                </div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1">
                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {ad.unique_impressions || 0}{ad.max_unique_impressions ? `/${ad.max_unique_impressions}` : ''} unique</span>
                  <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> {ad.total_clicks || 0}{ad.max_clicks ? `/${ad.max_clicks}` : ''} clicks</span>
                </div>
              </div>
              <Switch checked={ad.enabled} onCheckedChange={() => toggleEnabled(ad)} data-testid={`ad-toggle-${ad.ad_id}`} />
              <Button size="icon" variant="ghost" onClick={() => openEdit(ad)} data-testid={`ad-edit-${ad.ad_id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => remove(ad)} data-testid={`ad-delete-${ad.ad_id}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form.ad_id ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
            <DialogDescription>
              Caps are optional — leave at 0 for unlimited. When either cap is reached, the ad auto-leaves rotation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="ad-form-name" />
            </div>
            <div>
              <Label className="text-xs">Image URL *</Label>
              <Input placeholder="https://…/banner.png" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} data-testid="ad-form-image" />
              {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 max-h-28 rounded border" />}
            </div>
            <div>
              <Label className="text-xs">Click-thru URL</Label>
              <Input placeholder="https://…" value={form.click_url} onChange={e => setForm({ ...form, click_url: e.target.value })} data-testid="ad-form-click-url" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Max clicks (0 = ∞)</Label>
                <Input type="number" min={0} value={form.max_clicks} onChange={e => setForm({ ...form, max_clicks: parseInt(e.target.value) || 0 })} data-testid="ad-form-max-clicks" />
              </div>
              <div>
                <Label className="text-xs">Max unique impressions (0 = ∞)</Label>
                <Input type="number" min={0} value={form.max_unique_impressions} onChange={e => setForm({ ...form, max_unique_impressions: parseInt(e.target.value) || 0 })} data-testid="ad-form-max-impr" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="ad-form-enabled" />
              <Label className="text-xs">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="ad-form-save">
              {saving ? 'Saving…' : (form.ad_id ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
